import MySwitchField from "@components/form/switch";
import { RHFDatePicker, RHFTextField } from "@components/hook-form";
import {
  Box,
  Divider,
  Grid,
  InputLabel,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  resolveSectionOverrides,
  sortSectionFields,
} from "../../core/sections";
import type { IFormEngine } from "../../core/types";
import { useCommonStyles } from "../../hooks/useCommonStyles";

interface IDocumentInformationProps {
  translate: (text: any, options?: any) => string;
  engine: IFormEngine;
}

export const DocumentInformation = ({
  translate,
  engine,
}: IDocumentInformationProps) => {
  const { getValues, setValue } = useFormContext<any>();
  const { inputContainer } = useCommonStyles();
  const { palette } = useTheme();

  const { isFormReset } = engine;
  const isFrance = engine.config.complianceStandardKey === "FrancePA";

  const { hiddenFields, extraFields } = resolveSectionOverrides(
    engine.config,
    "document",
  );

  const hidden = new Set(hiddenFields);
  const isVisible = (key: string) => !hidden.has(key);
  const sortedExtras = sortSectionFields(extraFields ?? []);

  const { documentClass, documentDate, poNumber, deliveryNote } = getValues();

  const showPONumber = isVisible("poNumber");
  const showDeliveryNote = isVisible("deliveryNote");
  const [isPOVisible, setIsPOVisible] = useState(Boolean(poNumber));
  const [isDNVisible, setIsDNVisible] = useState(Boolean(deliveryNote));

  // France
  const _isPOVisible = isFrance || isPOVisible;
  const _isDNVisible = isFrance || isDNVisible;

  const getHeading = () => {
    switch (documentClass) {
      case "Invoice":
        return translate("invoice_information");
      case "PurchaseOrder":
        return translate("purchase_order_information");
      case "DeliveryNote":
        return translate("delivery_note_information");
      default:
        return translate("document_information");
    }
  };

  const showConnectedSection =
    (showPONumber && _isPOVisible) || (showDeliveryNote && _isDNVisible);

  useEffect(() => {
    if (isFormReset > 0) {
      setIsPOVisible(false);
      setIsDNVisible(false);
    }
  }, [isFormReset]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h6">{getHeading()}</Typography>
      </Grid>

      <Grid item xs={12}>
        <Box sx={inputContainer}>
          <Grid container spacing={2}>
            {isVisible("documentDate") && (
              <Grid item xs={12} md={6}>
                <InputLabel
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: palette.action.active,
                  }}
                >
                  {translate("document_date")}
                </InputLabel>
                <RHFDatePicker name="documentDate" disabled />
              </Grid>
            )}

            {documentClass !== "PurchaseOrder" &&
              documentClass !== "DeliveryNote" &&
              isVisible("dueDate") && (
                <Grid item xs={12} md={6}>
                  <RHFDatePicker name="dueDate" label={translate("due_date")} />
                </Grid>
              )}

            {/* Payment Terms for France */}
            {isFrance && documentClass === "Invoice" && (
              <Grid item xs={12}>
                <RHFTextField
                  name="paymentTerms"
                  label={translate("payment_terms")}
                  fullWidth
                />
              </Grid>
            )}

            {sortedExtras.map((field) => (
              <Grid
                key={field.id}
                item
                xs={field.grid?.xs ?? 12}
                sm={field.grid?.sm}
                md={field.grid?.md}
                lg={field.grid?.lg}
                xl={field.grid?.xl}
              >
                {field.render({ translate, engine })}
              </Grid>
            ))}

            {/* Toggle hide for France */}
            {documentClass !== "PurchaseOrder" && !isFrance && (
              <>
                {(documentClass === "Invoice" ||
                  documentClass === "DeliveryNote") &&
                  showPONumber && (
                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={
                        documentClass === "DeliveryNote"
                          ? { display: "flex", alignItems: "center" }
                          : {}
                      }
                    >
                      <Stack spacing={1}>
                        <MySwitchField
                          checked={isPOVisible}
                          label={
                            <Typography variant="body1">
                              {translate(`add_po_number`)}
                            </Typography>
                          }
                          onChange={() => {
                            const next = !isPOVisible;
                            setIsPOVisible(next);
                            setValue("isHeaderPOToggleOn", next);
                            if (!next) setValue("poNumber", null);
                          }}
                          labelPlacement="end"
                          sx={{ alignItems: "center" }}
                        />
                      </Stack>
                    </Grid>
                  )}

                {documentClass === "Invoice" && showDeliveryNote && (
                  <Grid item xs={12} md={6}>
                    <Stack spacing={1}>
                      <MySwitchField
                        checked={isDNVisible}
                        label={
                          <Typography variant="body1">
                            {translate(`add_delivery_note`)}
                          </Typography>
                        }
                        onChange={() => {
                          const next = !isDNVisible;
                          setIsDNVisible(next);
                          setValue("isHeaderDNToggleOn", next);
                          if (!next) setValue("deliveryNote", null);
                        }}
                        labelPlacement="end"
                        sx={{ alignItems: "center" }}
                      />
                    </Stack>
                  </Grid>
                )}
              </>
            )}

            {showConnectedSection && (
              <Grid container item xs={12} spacing={1}>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    {translate("connected_documents")}
                  </Typography>
                </Grid>
                {showPONumber && _isPOVisible && (
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      name="poNumber"
                      label={translate("po_number")}
                      placeholder={translate("add_po_number")}
                    />
                  </Grid>
                )}
                {showDeliveryNote && _isDNVisible && (
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      name="deliveryNote"
                      label={translate("delivery_note")}
                      placeholder={translate("add_delivery_note")}
                    />
                  </Grid>
                )}
              </Grid>
            )}
          </Grid>
        </Box>
      </Grid>
    </Grid>
  );
};
